import {
    Anchor,
    Box,
    Heading,
    Paragraph,
    Tag,
    TAG_APPEARANCES,
} from '@zillow/constellation';
import { PostReadTime } from '@zillow/zrs-cms';
import { uniqBy } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import { BaseTerm, Post } from '../../../types/shared';
import { wrap } from '../../../util/wrap';

interface PostHeaderProps {
    post: Post;
}

const isGray = (color: string): boolean =>
    ['gray', 'closeable', 'transparent'].includes(color);

const TAG_APPEARANCES_ARRAY = Object.values(TAG_APPEARANCES).filter(
    (color: string) => !isGray(color)
) as string[];

const StyledAnchor = styled(Anchor)`
    text-decoration: none;
`;

const PostHeader: React.FC<PostHeaderProps> = ({ post }) => {
    const excerpt = post.excerpt?.raw?.replace(/(<([^>]+)>)/gi, '') || '';
    const categories = uniqBy(
        [post.primaryCategory, ...post.categories],
        'id'
    ).filter((category): category is BaseTerm => Boolean(category));

    return (
        <Box
            sx={{
                maxWidth: '$breakpoints.lg',
                width: '100%',
                margin: '0',
                '@media $xl': {
                    paddingRight: '$spacing.xxl',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '$spacing.xs',
                    justifyContent: 'flex-start',
                    marginBottom: '$spacing.sm',
                    '@media $lg_gte': {
                        gap: '$spacing.sm',
                    },
                    '@media $xxl_gte': {
                        justifyContent: 'flex-start',
                        marginBottom: '$spacing.lg',
                        textAlign: 'left',
                    },
                }}
            >
                {categories?.map((category, index) => (
                    <StyledAnchor key={category.id} href={category.link}>
                        <Tag
                            textTransform="none"
                            appearance={
                                TAG_APPEARANCES_ARRAY[
                                    wrap(
                                        0,
                                        TAG_APPEARANCES_ARRAY.length - 1,
                                        index
                                    )
                                ]
                            }
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: category.name,
                                }}
                            />
                        </Tag>
                    </StyledAnchor>
                ))}
                <PostReadTime readTime={post.readTime} />
            </Box>
            <Box
                sx={{
                    paddingRight: '$spacing.md',
                    '@media $xxl': {
                        textAlign: 'left',
                    },
                }}
            >
                <Heading
                    level={1}
                    fontType={{
                        default: 'h4',
                        lg: 'h3',
                    }}
                    marginBottom={{
                        default: 'sm',
                        xxl: 'lg',
                    }}
                    data-epi-type="title"
                    dangerouslySetInnerHTML={{ __html: post.title?.raw }}
                />
                {!!excerpt && (
                    <Paragraph
                        fontColor="textMedium"
                        fontType={{
                            default: 'body',
                            lg: 'large',
                        }}
                        marginBottom={{
                            default: 'md',
                            lg: 'lg',
                            xxl: 'lg',
                        }}
                        dangerouslySetInnerHTML={{ __html: excerpt }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default PostHeader;
